/**
 * Global state management with Zustand
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { Market, User, Bet } from "../api/types";
import type { ToastMessage } from "../components/Toast";

export type Theme = "light" | "dark";

interface AppState {
  // Current session
  deviceId: string;
  market: Market | null;
  user: User | null;
  users: User[];
  bets: Bet[];

  // Theme preference
  theme: Theme;

  // Toast notifications
  toasts: ToastMessage[];

  // Persisted session IDs for restoration
  currentMarketId: string | null;
  currentUserId: string | null;

  // Actions
  setDeviceId: (deviceId: string) => void;
  setMarket: (market: Market | null) => void;
  setUser: (user: User | null) => void;
  setUsers: (users: User[]) => void;
  setBets: (bets: Bet[]) => void;
  addUser: (user: User) => void;
  updateBet: (bet: Bet) => void;
  setSession: (marketId: string | null, userId: string | null) => void;
  setTheme: (theme: Theme) => void;
  addToast: (toast: Omit<ToastMessage, "id">) => string;
  dismissToast: (id: string) => void;
  reset: () => void;
}

// Generate device ID if not exists
function getOrCreateDeviceId(): string {
  const stored = localStorage.getItem("cazino_device_id");
  if (stored) return stored;

  const newId = crypto.randomUUID();
  localStorage.setItem("cazino_device_id", newId);
  return newId;
}

// Get initial theme from stored value, defaulting to light
function getInitialTheme(): Theme {
  const stored = localStorage.getItem("cazino-theme");
  if (stored === "dark" || stored === "light") return stored;
  return "light";
}

export const useStore = create<AppState>()(
  persist(
    (set) => ({
      deviceId: getOrCreateDeviceId(),
      market: null,
      user: null,
      users: [],
      bets: [],
      theme: getInitialTheme(),
      toasts: [],
      currentMarketId: null,
      currentUserId: null,

      setDeviceId: (deviceId) => {
        localStorage.setItem("cazino_device_id", deviceId);
        set({ deviceId });
      },

      setMarket: (market) => set({ market }),
      setUser: (user) => set({ user }),
      setUsers: (users) => set({ users }),
      setBets: (bets) => set({ bets }),

      addUser: (user) =>
        set((state) => ({
          users: state.users.some((u) => u.id === user.id)
            ? state.users
            : [...state.users, user],
        })),

      updateBet: (bet) =>
        set((state) => ({
          bets: state.bets.map((b) => (b.id === bet.id ? bet : b)),
        })),

      setSession: (marketId, userId) =>
        set({ currentMarketId: marketId, currentUserId: userId }),

      setTheme: (theme) => {
        localStorage.setItem("cazino-theme", theme);
        document.documentElement.setAttribute("data-theme", theme);
        set({ theme });
      },

      addToast: (toast) => {
        const id = crypto.randomUUID();
        set((state) => ({
          toasts: [...state.toasts, { ...toast, id }],
        }));
        return id;
      },

      dismissToast: (id) =>
        set((state) => ({
          toasts: state.toasts.filter((t) => t.id !== id),
        })),

      reset: () =>
        set({
          market: null,
          user: null,
          users: [],
          bets: [],
          toasts: [],
          currentMarketId: null,
          currentUserId: null,
        }),
    }),
    {
      name: "cazino-session",
      partialize: (state) => ({
        deviceId: state.deviceId,
        currentMarketId: state.currentMarketId,
        currentUserId: state.currentUserId,
        theme: state.theme,
      }),
    },
  ),
);
